import * as React from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { Avatar, AvatarImage, AvatarFallback, AvatarGroup } from "../components/ui/avatar"
import { ElementRenderer } from "./element-renderer"
import { cn } from "../lib/utils"

/**
 * Avatar renderer — displays a circular avatar image with fallback initials.
 *
 * JSON shape:
 * {
 *   "type": "avatar",
 *   "src": "https://...",
 *   "alt": "User Name",
 *   "size": "default" | "sm" | "lg",
 *   "fallback": "JD",
 *   "className": "..."
 * }
 */

/** Map legacy size tokens to cn-avatar data-size values */
const SIZE_MAP: Record<string, "default" | "sm" | "lg"> = {
  xs: "sm",
  sm: "sm",
  md: "default",
  default: "default",
  lg: "lg",
  xl: "lg",
}

export function AvatarRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("avatar")
  const resolvedVariant = (element as { variant?: string }).variant
    ?? atomDefaults.defaultVariant
    ?? "default"

  const src = element.src ? String(resolveSymbol(element.src) ?? "") : ""
  const alt = element.alt ? String(resolveSymbol(element.alt) ?? "") : ""
  const fallback = element.fallback ? String(resolveSymbol(element.fallback) ?? "") : ""
  const sizeToken = element.size ?? element.options?.size ?? atomDefaults.defaultSize ?? "default"

  const size = typeof sizeToken === "string"
    ? SIZE_MAP[sizeToken] ?? "default"
    : "default"

  const inlineStyle: React.CSSProperties = {}
  if (typeof sizeToken === "number") {
    inlineStyle.width = sizeToken
    inlineStyle.height = sizeToken
  }
  // Apply styling props (marginLeft for stacked avatars, border)
  const styling = element.styling as Record<string, unknown> | undefined
  if (styling?.marginLeft) inlineStyle.marginLeft = `${styling.marginLeft}px`
  if (styling?.border) {
    const border = styling.border as Record<string, unknown>
    if (border.width) {
      inlineStyle.borderWidth = `${border.width}px`
      inlineStyle.borderStyle = "solid"
      inlineStyle.borderColor = border.color ? String(resolveSymbol(String(border.color)) ?? "var(--background)") : "var(--background)"
    }
  }
  const hasStyle = Object.keys(inlineStyle).length > 0

  // Derive initials from alt text if no fallback specified
  const initials = fallback || alt.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()

  return (
    <Avatar
      size={size}
      className={cn(element.className)}
      style={hasStyle ? inlineStyle : undefined}
      data-atom-type="avatar"
      data-variant={resolvedVariant}
      data-size={typeof sizeToken === "string" ? sizeToken : "default"}
    >
      {src && <AvatarImage src={src} alt={alt} />}
      <AvatarFallback>{initials}</AvatarFallback>
    </Avatar>
  )
}

export function AvatarGroupRenderer({ element }: RendererProps) {
  return (
    <AvatarGroup className={cn(element.className)}>
      {element.children?.map((child) => (
        <ElementRenderer key={child.id} element={child} />
      ))}
    </AvatarGroup>
  )
}
